/**
 * 🐋 Advertiser Content System Constants - Cetalingua FM
 * Target path: /src/shared/advertiserConstants.js
 * 
 * Shared between:
 * - Advertiser Dashboard (React Web)
 * - Admin Dashboard (React Web)
 * - React Native App
 * - Cloud Functions
 * 
 * NOTE: Species uses numeric IDs (1-88) from speciesData.js.
 * Import speciesData.js for full species list — this file only has content system constants.
 */

// ============================================================================
// CONTENT TYPES
// ============================================================================

export const CONTENT_TYPES = [
  { 
    id: 'featured_media', 
    name: 'Featured Media', 
    emoji: '📸', 
    desc: 'Hero image or video showcasing your tours',
    requiresMedia: true,
    requiresBody: false,
  },
  { 
    id: 'whats_new', 
    name: "What's New", 
    emoji: '📰', 
    desc: 'Share a company update or news blurb',
    requiresMedia: false,
    requiresBody: true,
  },
  { 
    id: 'hot_deal', 
    name: 'Hot Deal', 
    emoji: '🔥', 
    desc: 'Exclusive offer or discount for app users',
    requiresMedia: false,
    requiresBody: true,
  },
];

export const CONTENT_TYPE_MAP = Object.fromEntries(
  CONTENT_TYPES.map(t => [t.id, t])
);

// ============================================================================
// STATUS
// ============================================================================

export const CONTENT_STATUS = {
  DRAFT: 'draft',
  PENDING: 'pending',
  APPROVED: 'approved',
  REJECTED: 'rejected',
  EXPIRED: 'expired',
};

export const PUSH_STATUS = {
  PENDING: 'pending',
  APPROVED: 'approved',
  REJECTED: 'rejected',
  SENT: 'sent',
};

// ============================================================================
// CADENCE
// ============================================================================

export const CADENCE = {
  WEEKLY: 'weekly',   // Paid advertisers
  MONTHLY: 'monthly', // Free/sticker advertisers
};

/**
 * Get the current period string based on cadence type.
 * Weekly: "2026-W07"
 * Monthly: "2026-02"
 */
export function getCurrentPeriod(cadence, date = new Date()) {
  if (cadence === CADENCE.WEEKLY) {
    return getWeekPeriod(date);
  }
  return getMonthPeriod(date);
}

/**
 * ISO 8601 week string: "2026-W07"
 */
export function getWeekPeriod(date = new Date()) {
  const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  d.setUTCDate(d.getUTCDate() + 4 - (d.getUTCDay() || 7));
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  const weekNo = Math.ceil((((d - yearStart) / 86400000) + 1) / 7);
  return `${d.getUTCFullYear()}-W${String(weekNo).padStart(2, '0')}`;
}

/**
 * Month period string: "2026-02"
 */
export function getMonthPeriod(date = new Date()) {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
}

/**
 * Get the next period start date for countdown display.
 */
export function getNextPeriodDate(cadence, date = new Date()) {
  if (cadence === CADENCE.WEEKLY) {
    const next = new Date(date);
    const dayOfWeek = next.getDay();
    const daysUntilMonday = dayOfWeek === 0 ? 1 : (8 - dayOfWeek);
    next.setDate(next.getDate() + daysUntilMonday);
    next.setHours(0, 0, 0, 0);
    return next;
  }
  return new Date(date.getFullYear(), date.getMonth() + 1, 1);
}

/**
 * Determine cadence from subscription type.
 */
export function getCadenceForTier(subscriptionType) {
  return subscriptionType === 'paid' ? CADENCE.WEEKLY : CADENCE.MONTHLY;
}

/**
 * Category display info for species grouping in UI.
 * Used by SpeciesSelectScreen, CompanyProfile, admin panel.
 */
export const SPECIES_CATEGORIES = [
  { id: 'whale',    name: 'Whales',              emoji: '🐋' },
  { id: 'dolphin',  name: 'Dolphins',            emoji: '🐬' },
  { id: 'porpoise', name: 'Porpoises',           emoji: '🐬' },
  { id: 'manatee',  name: 'Manatees & Dugongs',  emoji: '🦭' },
  { id: 'unknown',  name: 'Unidentified',        emoji: '❓' },
];

// ============================================================================
// LIMITS
// ============================================================================

export const LIMITS = {
  TITLE_MAX: 100,
  BODY_MAX: 500,
  PUSH_TITLE_MAX: 50,
  PUSH_BODY_MAX: 150,
  DEAL_CODE_MAX: 30,
  DEAL_DISCOUNT_MAX: 50,
  MAX_MEDIA_SIZE_MB: 10,
  MAX_VIDEO_DURATION_SEC: 60,
  MAX_IMAGES_PER_PROFILE: 5,
};

// ============================================================================
// FIREBASE STORAGE PATHS
// ============================================================================

export const STORAGE_PATHS = {
  ADVERTISER_CONTENT: (userId, filename) => 
    `advertiser-content/${userId}/${Date.now()}_${filename}`,
  COMPANY_IMAGES: (userId, filename) => 
    `company-images/${userId}/${Date.now()}_${filename}`,
  PROOF_UPLOADS: (userId, filename) => 
    `proof-uploads/${userId}/${Date.now()}_${filename}`,
};

// ============================================================================
// LEGACY LISTING TYPES (backward compatibility during migration)
// ============================================================================

export const LEGACY_LISTING_TYPES = [
  { id: 'campaign', name: 'Campaign', emoji: '📢' },
  { id: 'offer',    name: 'Offer',    emoji: '🎁' },
  { id: 'sale',     name: 'Sale',     emoji: '🏷️' },
  { id: 'update',   name: 'Update',   emoji: '📰' },
];
